import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MED_PASS_FLAG_TONES, type MedPassFlag } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { SearchField } from "@/components/app/patterns/search-field";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportTable } from "@/components/app/documents/report-table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFacility } from "@/lib/facility/facility";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { orpc } from "@/lib/orpc";
import {
  optionalDay,
  optionalOneOf,
  optionalString,
  KEEP_PLACE,
  optionalSearch,
} from "@/lib/search";
import { useOptionalPolicy } from "@/lib/policy";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";
import { FlagLegend } from "./-components/flag-legend";
import { ReportActions } from "./-components/report-actions";
import { facilityDay } from "./-components/report-dates";
import { ReportFilters, type Concern } from "./-components/report-filters";
import { ReportSummary } from "./-components/report-summary";
import { ResidentTable } from "./-components/resident-table";
import { SweepOffNotice } from "./-components/sweep-off-notice";
import { Charted } from "@/routes/_authenticated/_pharmacy/med-pass-report/-components/med-pass-report-charted";
import type { ReportColumn } from "@/components/app/documents/report-table";
import type { Session } from "@/routes/_authenticated/_pharmacy/med-pass-report/-components/report-types";

type ReportSearch = {
  start?: string;
  end?: string;
  window?: string;
  view?: "sessions";
  q?: string;
  concern?: string;
};

export const Route = createFileRoute("/_authenticated/_pharmacy/med-pass-report/")({
  validateSearch: (search: Record<string, unknown>): ReportSearch => {
    const concern = optionalString(search.concern);
    return {
      start: optionalDay(search.start),
      end: optionalDay(search.end),
      window: optionalString(search.window)?.slice(0, 60) || undefined,
      view: optionalOneOf(["sessions"] as const, search.view),
      q: optionalSearch(search.q),
      concern:
        concern && (concern === "any" || concern in MED_PASS_FLAG_TONES) ? concern : undefined,
    };
  },
  component: MedPassReport,
});

const SESSION_COLUMNS: ReadonlyArray<ReportColumn<Session>> = [
  { header: "Day", cell: (s) => s.localDate },
  { header: "Pass", cell: (s) => s.windowName },
  { header: "Residents", cell: (s) => s.residents, align: "right" },
  { header: "Scheduled", cell: (s) => s.scheduled, align: "right" },
  { header: "Charted", cell: (s) => s.charted, align: "right" },
  { header: "Exceptions", cell: (s) => s.exceptions, align: "right" },
  { header: "Missed", cell: (s) => s.missed, align: "right" },
  { header: "Open", cell: (s) => s.unanswered, align: "right" },
  { header: "Late", cell: (s) => s.late, align: "right" },
  {
    header: "Charted %",
    cell: (s) => <Charted value={s.chartedPct} />,
    value: (s) => s.chartedPct,
    align: "right",
  },
  {
    header: "Avg lag",
    cell: (s) => (s.avgLagMinutes === null ? "-" : `${s.avgLagMinutes}m`),
    value: (s) => s.avgLagMinutes,
    align: "right",
  },
  {
    header: "Charted by",
    cell: (s) => s.staff.map((person) => `${person.name} (${person.doses})`).join(", ") || "-",
    value: (s) => s.staff.map((person) => person.name).join(" "),
    clamp: "lg",
  },
];

/** How the pass actually went: the doses that were owed, not the rows that exist. */
function MedPassReport() {
  const { facilityId } = useFacility();
  const timezone = useOptionalPolicy()?.me?.facilityTimezones?.[facilityId] ?? "America/New_York";
  const day = (offset: number) => facilityDay(offset, timezone);
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ReportSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const startDate = params.start ?? day(-6);
  const endDate = params.end ?? day(0);
  const windowId = params.window ?? "all";
  const setWindowId = (next: string) => patch({ window: next === "all" ? undefined : next });
  const view: "report" | "sessions" = params.view ?? "report";
  const setView = (next: "report" | "sessions") =>
    patch({ view: next === "sessions" ? "sessions" : undefined });
  const { text: search, change: setSearch } = useSearchParamText(params.q, (q) => patch({ q }));
  /* "any" is the question managers actually ask — show me whoever needs
     looking at — and a single flag is the follow-up to it. */
  const concern = (params.concern ?? "all") as Concern;
  const setConcern = (next: Concern) => patch({ concern: next === "all" ? undefined : next });

  const realFacility = facilityId === "all" ? null : facilityId;
  const input = {
    facilityId: realFacility ?? "",
    startDate,
    endDate,
    windowId: windowId === "all" ? null : windowId,
    /* The drill-down is the point of the report: a number nobody can open is
       a number people argue with. */
    includeLines: true,
  };

  const windows = useQuery({
    ...orpc.pharmacy.medPass.windows.queryOptions({ input: { facilityId: realFacility ?? "" } }),
    enabled: realFacility !== null,
  });
  const report = useQuery({
    ...orpc.pharmacy.medPass.sessionReport.queryOptions({ input }),
    enabled: realFacility !== null,
  });

  const summary = report.data?.summary;
  const term = search.trim().toLowerCase();
  const rows = (report.data?.rows ?? []).filter((row) => {
    if (
      term &&
      !row.residentName.toLowerCase().includes(term) &&
      !(row.roomNumber ?? "").toLowerCase().includes(term)
    )
      return false;
    if (concern === "any")
      return row.flags.some((flag) => MED_PASS_FLAG_TONES[flag as MedPassFlag] !== "info");
    if (concern !== "all") return row.flags.includes(concern);
    return true;
  });
  const cron = report.data?.cronHealth;

  return (
    <>
      <PageHeader
        title="Med pass report"
        meta="The doses that were owed, and what happened to each one."
        actions={
          realFacility === null ? null : (
            <ReportActions input={input} view={view} windows={windows.data ?? []} />
          )
        }
      />

      {realFacility === null ? (
        <OneBuildingCard prompt="Choose a building. Each building keeps its own pass windows, so the report runs for one at a time." />
      ) : (
        <>
          {cron && !cron.enabled ? (
            <SweepOffNotice lastMissedAt={cron.lastMissedAt} timezone={timezone} />
          ) : null}

          <ReportFilters
            day={day}
            startDate={startDate}
            endDate={endDate}
            onRange={(start, end) =>
              patch({
                start: start === day(-6) ? undefined : start,
                end: end === day(0) ? undefined : end,
              })
            }
            windows={windows.data ?? []}
            windowId={windowId}
            onWindow={setWindowId}
            concern={concern}
            onConcern={setConcern}
          />

          {report.isError ? (
            /* Every tile and table below would otherwise read as a clean pass. */
            <ReadFailed
              message="Couldn't run the report for this range."
              error={report.error}
              retrying={report.isFetching}
              onRetry={() => void report.refetch()}
            />
          ) : (
            <>
              <ReportSummary summary={summary} pending={report.isPending} />

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Tabs
                    value={view}
                    onValueChange={(next) => setView(next as "report" | "sessions")}
                  >
                    <TabsList>
                      <TabsTrigger value="report">
                        By resident
                        {report.data ? (
                          <span className="text-muted-foreground ml-1.5 tabular-nums">
                            {rows.length}
                          </span>
                        ) : null}
                      </TabsTrigger>
                      <TabsTrigger value="sessions">
                        By pass session
                        {report.data ? (
                          <span className="text-muted-foreground ml-1.5 tabular-nums">
                            {report.data.sessions.length}
                          </span>
                        ) : null}
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  {view === "report" ? <FlagLegend /> : null}
                </div>
                {/* The search narrows the resident table, so it sits with it. */}
                {view === "report" ? (
                  <SearchField
                    value={search}
                    onChange={setSearch}
                    placeholder="Find a resident or room…"
                  />
                ) : null}
              </div>

              {view === "report" ? (
                <ResidentTable
                  rows={rows}
                  dates={report.data?.localDates ?? []}
                  linesByResident={report.data?.linesByResident}
                  pending={report.isPending}
                  filtered={Boolean(report.data?.rows.length) && rows.length === 0}
                />
              ) : (
                <>
                  <ReportTable
                    rows={report.data?.sessions}
                    pending={report.isPending}
                    filename="med-pass-sessions.csv"
                    rowKey={(row) => row.key}
                    noun="pass sessions"
                    columns={SESSION_COLUMNS}
                    empty={{
                      title: "No passes",
                      description: "No pass ran in this range.",
                    }}
                  />
                  <p className="text-muted-foreground text-xs">
                    Staff counts are here to spot a shift that needs support or training, not to
                    rank people. Average lag is the mean time between a dose falling due and it
                    being charted.
                  </p>
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
